import { useId } from 'react';
import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { cn } from '@/lib/cn';
import { SPRING_SNAPPY } from '@/lib/motion';

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  count?: number;
}

interface TabsProps<T extends string> {
  items: TabItem<T>[];
  value: T;
  onValueChange: (v: T) => void;
  className?: string;
  /** For a row that can outgrow one line (e.g. 5+ items) — wraps onto a
   * second row as individually-bordered pills instead of staying in one
   * shared pill and either overflowing or relying on horizontal scroll,
   * which has no visible affordance (or touch-swipe hint) on desktop. */
  wrap?: boolean;
}

/** Segmented control. Roving focus via native buttons + arrow keys. The active
 * background is a shared-layout pill that slides between tabs (via Framer
 * Motion's `layoutId`) instead of cutting from button to button; `wrap`'s
 * individually-bordered chips keep their existing instant treatment since
 * there's no single track for a pill to slide along. */
export function Tabs<T extends string>({
  items,
  value,
  onValueChange,
  className,
  wrap = false,
}: TabsProps<T>) {
  const pillId = useId();
  return (
    <div
      role="tablist"
      className={cn(
        'flex gap-1',
        wrap ? 'w-full flex-wrap' : 'w-fit rounded-pill border-2 border-ink bg-surface p-1',
        className,
      )}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            role="tab"
            aria-selected={active}
            onClick={() => onValueChange(item.value)}
            className={cn(
              'relative inline-flex items-center gap-1.5 rounded-pill px-4 py-1.5 font-display text-sm font-semibold',
              wrap && 'border-2',
              active
                ? wrap
                  ? 'border-ink bg-ink text-on-solid'
                  : 'text-on-solid'
                : cn(
                    'text-ink-soft transition-colors hover:text-ink',
                    wrap && 'border-hairline-strong hover:border-ink',
                  ),
            )}
          >
            {!wrap && active && (
              <motion.span
                layoutId={`${pillId}-active-pill`}
                className="absolute inset-0 rounded-pill bg-ink"
                transition={SPRING_SNAPPY}
              />
            )}
            <span className="relative inline-flex items-center gap-1.5">
              {item.label}
              {item.count != null && item.count > 0 && (
                <span
                  className={cn(
                    'inline-flex min-w-5 items-center justify-center rounded-pill px-1 text-xs',
                    active ? 'bg-on-solid text-ink' : 'bg-accent text-on-accent',
                  )}
                >
                  {item.count}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
